import { CONSTANTS } from '../../constants/constants';
import { encodeParam } from '../../services/params.utils';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import NRAssetRiskChanged from '../NRAssetRiskChanged/NRAssetRiskChanged';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { removeTabAndSubTabFromFilePath } from '../../services/urlInterceptor.utils';
import { StatusFormatter } from '../NRMisconfigurationTable/NRMisconfigurationTableCustomComponents/NRMisconfigurationStatusProvider/NRMisconfigurationStatusProvider';
import { camelCaseToHuman } from '../../utils/utils';

const { BINARIES, CREDENTIALS, LICENSE_ISSUES, CRYPTOGRAPHY, FILE_SYSTEM, SBOM, VULNERABILITIES, MISCONFIGURATIONS } = CONSTANTS.REPORT.TABS;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;
const { EXPOSED_SECRETS } = CONSTANTS.REPORT.CREDENTIALS_SUB_TABS;

function NRActivityFeedItemHeader({ item, handleClose }) {
  const theme = useTheme();
  const { entity } = item;
  const location = useLocation();
  const activity = item?.activityType?.split('_').at(-1).toLowerCase();

  const navigate = useNavigate();

  const handleClick = () => {
    navigate(handleTableRedirect(entity, entity.__typename));
    handleClose(); // close after navigation
  };
  const getEntityLabel = () => {
    return (
      entity?.cve ||
      entity?.checkName ||
      entity?.name ||
      entity?.componentName ||
      entity?.userName ||
      entity?.hash ||
      entity?.checkId ||
      entity?.groupId
    );
  };

  const handleLinkActivate = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const interactiveLinkProps = {
    role: 'link',
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleLinkActivate,
    'aria-label': `${getEntityLabel()} ${activity}`
  };

  const handleTableRedirect = (entity, typename) => {
    let redirectToBaseURL = removeTabAndSubTabFromFilePath(location);
    let extraParams = new URLSearchParams();
    let tab = '';
    let subTab = '';
    switch (typename) {
      case 'VulnerabilityUpdatedPayload':
      case 'VulnerabilityAddedPayload':
      case 'VulnerabilityRemediatedPayload':
        tab = VULNERABILITIES;
        extraParams.append(
          'vulnerabilityColumnFilter',
          encodeParam([
            {
              fieldName: 'CVE',
              value: entity.cve,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'MisconfigChangedPayload':
        tab = MISCONFIGURATIONS;
        extraParams.append(
          'misconfigurationColumnsFilter',
          encodeParam([
            {
              fieldName: 'DISPLAYNAME',
              value: entity.checkName,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'IdentificationAddedPayload':
        tab = SBOM;
        extraParams.append(
          'componentColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'NAME',
                value: entity.name,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VENDOR',
                value: entity.vendor,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VERSION',
                value: entity.version,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value) // removes falsy values like '', null, undefined
          )
        );
        break;
      case 'SecretRemediatedPayload':
        tab = CREDENTIALS;
        subTab = encodeParam(EXPOSED_SECRETS);
        extraParams.append('stringFilter', encodeParam(entity.filePath));
        break;
      case 'LicenseIssueRemediatedPayload':
        tab = LICENSE_ISSUES;
        extraParams.append(
          'licenseIssuesColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'COMPONENTNAME',
                value: entity.componentName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'ISSUENAME',
                value: entity.issueName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value) // removes falsy values like '', null, undefined
          )
        );
        break;
      default:
        break;
    }
    return `${redirectToBaseURL}${!!tab ? '/' + tab : ''}${!!subTab ? '/' + subTab : ''}?${extraParams.toString()}`;
  };

  switch (entity.__typename) {
    case 'VulnerabilityRemediatedPayload':
    case 'VulnerabilityUpdatedPayload':
    case 'VulnerabilityAddedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography
            {...interactiveLinkProps}
            variant="body2"
            sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
          >
            {entity.cve}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
        </Box>
      );
    case 'VulnerabilityRemovedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {entity?.cve}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
        </Box>
      );
    case 'MisconfigChangedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography
            {...interactiveLinkProps}
            variant="body2"
            sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
          >
            {entity.checkName}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1, whiteSpace: 'nowrap' }} variant="body2" children={` ${activity} to `} />
          <StatusFormatter value={entity?.newStatus} uppercase={entity?.newStatus?.toLowerCase() === 'na'} small={true} />
        </Box>
      );
    case 'IdentificationAddedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography
            {...interactiveLinkProps}
            variant="body2"
            sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
          >
            {entity.name}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1, color: 'red' }} variant="body2" children={activity} />
        </Box>
      );
    case 'IdentificationRemovedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {entity?.name}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1, color: 'red' }} variant="body2" children={activity} />
        </Box>
      );
    case 'AssetGroupRemovedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {entity?.name}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
        </Box>
      );
    case 'AssetRiskChangedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {entity?.name}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
          <NRAssetRiskChanged sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }} oldRisk={entity.oldRisk} newRisk={entity.newRisk} />
        </Box>
      );
    case 'SecretRemediatedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography
            variant="body2"
            sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
            {...interactiveLinkProps}
          >
            {camelCaseToHuman(entity?.subtype)}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1, cursor: 'default' }} variant="body2" children={'remediated'} />
        </Box>
      );
    case 'LicenseIssueRemediatedPayload':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisTypography
            variant="body2"
            sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
            {...interactiveLinkProps}
          >
            {camelCaseToHuman(entity?.issueName)}
          </EllipsisTypography>
          <Typography sx={{ ml: 0.5, mr: 1, cursor: 'default' }} variant="body2" children={'remediated'} />
        </Box>
      );
    default:
      break;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pb: 0.5 }}>
      <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }}>{`${
        entity?.name || entity?.componentName || entity?.userName || entity?.hash || entity?.checkId || entity?.groupId
      }`}</EllipsisTypography>
      <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
    </Box>
  );
}
export default NRActivityFeedItemHeader;
